/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useContext } from 'react';
import {
  Button as ButtonCore,
  type ButtonProps as ButtonCoreProps,
  type ButtonRenderProps as ButtonCoreRenderProps,
  type ContextValue,
  DEFAULT_SLOT,
  FieldErrorContext as FieldErrorContextCore,
  Provider,
  SelectValue as SelectValueCore,
  useContextProps
} from 'react-aria-components';
import { ChevronDownSmallStrokeIcon } from '../../icons/chevron-down-small-stroke/chevron-down-small-stroke.icon';
import { $ } from '../../tokens.stylex';
import { Icon, IconContext } from '../icon/icon.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';

interface SelectInputStyleProps {
  /** The size variant of the input. (Default: 48) */
  size?: keyof typeof sizeVariants;
  style?: stylex.StyleXStyles;
  /** Used to add accessories to the input. */
  children?: ((values: ButtonCoreRenderProps & { defaultChildren: ReactNode }) => ReactNode);
}

type SelectInputProps = Omit<ButtonCoreProps, 'style' | 'className' | 'children'> & SelectInputStyleProps;

const SelectInputStyleContext = createContext<ContextValue<SelectInputStyleProps, HTMLButtonElement>>({});

const SelectInput = forwardRef((props: SelectInputProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, SelectInputStyleContext);

  const validation = useContext(FieldErrorContextCore);

  const { style, size = 48, children, ...coreProps } = props;

  return (
    <ButtonCore
      ref={ref}
      data-invalid={validation?.isInvalid || null}
      {...stylex.props(styles.base, sizeVariants[size], style)}
      {...coreProps}>
      {(renderProps) => {
        renderProps.defaultChildren = (
          <SelectValueCore
            data-hovered={renderProps.isHovered || null}
            {...stylex.props(valueStyles.base, renderProps.isDisabled && valueStyles.disabled)}>
            {/* Fallback to default children if no aria-label or textValue is provided */}
            {(selectValueRenderProps) => selectValueRenderProps.selectedText ?? selectValueRenderProps.defaultChildren}
          </SelectValueCore>
        );

        return (
          <Provider values={[
            [ProgressIndicatorContext, {
              size: size === 48 ? 20 : 16,
              variant: 'primary'
            }],
            [IconContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [SELECT_INPUT_SLOTS.TRIGGER_ICON]: {
                  style: [iconStyles.base, renderProps.isPressed && iconStyles.rotated],
                  size: size === 48 ? 20 : 16,
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    ((renderProps.isHovered || renderProps.isFocused || renderProps.isPressed) && 'interactive') ||
                    'quaternary'
                  ),
                  children: <ChevronDownSmallStrokeIcon />,
                  'aria-hidden': true,
                }
              }
            }]]}>
            {children?.(renderProps) ||
              <>
                {renderProps.defaultChildren}
                <Icon slot={SELECT_INPUT_SLOTS.TRIGGER_ICON} />
              </>
            }
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const styles = stylex.create({
  base: {
    borderColor: {
      default: $.border16,
      ':is([data-disabled])': $.border10,
      ':is([data-focused]):not([data-invalid])': $.borderInteractive,
      ':is([data-hovered]):not([data-focused], [data-invalid])': $.border24,
      ':is([data-invalid])': $.borderCritical,
      ':is([data-pressed]):not([data-invalid])': $.borderInteractive,
    },
    borderStyle: 'solid',
    borderWidth: {
      default: 1,
      ':is([data-disabled])': 1,
      ':is([data-focused], [data-pressed], [data-invalid])': 2,
    },
    gap: 12,
    outline: 'none',
    overflow: 'hidden',
    alignItems: 'center',
    backgroundColor: $.surfaceBGPrimary,
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    fontWeight: 400,
    minWidth: 0,
    width: '100%',
  },
});

const sizeVariants = stylex.create({
  48: {
    borderRadius: 10,
    gap: 12,
    fontSize: 16,
    letterSpacing: -0.5,
    height: 48,
    paddingBottom: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
    paddingLeft: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingRight: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingTop: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
  },
  32: {
    borderRadius: 8,
    gap: 6,
    fontSize: 14,
    letterSpacing: -0.45,
    height: 32,
    paddingLeft: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
    paddingRight: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
  },
});

const valueStyles = stylex.create({
  base: {
    flex: '1 1 0',
    outline: 'none',
    overflow: 'hidden',
    color: {
      default: $.textPlaceholder,
      ':is([data-hovered]):is([data-placeholder])': $.textQuaternary,
      ':is([data-placeholder])': $.textPlaceholder,
      ':not([data-placeholder])': $.textPrimary,
    },
    display: 'flex',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    width: '100%',
  },
  disabled: {
    color: $.textDisabled,
  }
});

const iconStyles = stylex.create({
  base: { transition: '0.15s' },
  rotated: {
    transform: 'rotate(180deg)',
    transformOrigin: 'center center',
  }
});

const SELECT_INPUT_SLOTS = {
  TRIGGER_ICON: 'select-trigger-icon',
};

export { SELECT_INPUT_SLOTS, SelectInput, SelectInputStyleContext, type SelectInputProps, type SelectInputStyleProps };
